import { useCallback, useEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CircularProgress, FormControlLabel, MenuItem, Radio, RadioGroup } from '@material-ui/core';
import _ from 'lodash';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, SORT_ASC, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import {
    DATASOURCE_TYPE,
    getAddButtonText,
    getDialogTableColumns,
    getDialogTableDataSource,
    getEmptyGridText,
    getSnmpVersionOrDefault,
    isSnmpV2c,
    isSnmpV3,
    NOTIFY_NOTIFY_IDS_FIELD,
    SELECTION_MODE,
    SNMP_AUTHPASSWORD_VALUE_FIELD,
    SNMP_COMMUNITY_VALUE_FIELD,
    SNMP_CONTEXT_FIELD,
    SNMP_PRIVACYPASSWORD_VALUE_FIELD,
    SNMP_PRIVACYPROTOCOL_FIELD,
    SNMP_PROTOCOL_FIELD,
    SNMP_SERVER_FIELD,
    SNMP_USERNAME_FIELD,
    SNMP_VERSION_DEFAULT,
    SNMP_VERSION_FIELD,
    SNMP_VERSION_OPTIONS,
    SNMPV3_PRIVACY_PROTOCOLS,
    SNMPV3_PROTOCOLS,
} from './NotificationPolicyConstants';
import ListSelectorField from '../../form/ListSelectorField';
import { NAME_FIELD } from '../service-tests/ServiceTestAdminConstants';
import { useSettingStyles } from './NotificationPolicySettingsStyles';
import NgpSelect from '../../form/NgpSelect';
import NgpPasswordField from '../../form/NgpPasswordField';
import IPAddressUtilities from '../../../utilities/IPAddressUtilities';

const formId = 'notificationFrmSnmp';
const addButtonText = t`Ok`;
const updateButtonText = t`Update`;

const defaultFormValues = {
    all: true,
    version: getSnmpVersionOrDefault(SNMP_VERSION_DEFAULT),
    community: {},
    userName: '',
    context: '',
    protocol: 'None',
    authPassword: {},
    privacyProtocol: 'None',
    privacyPassword: {},
    selectionMode: SELECTION_MODE.all,
};

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

const TRAP_EXIST_ERROR_MESSAGE = i18n._(t`This SNMP trap already exists`);
const VALID_SERVER_ERROR_MESSAGE = i18n._(t`Please enter a valid server`);
const VALUE_REQUIRED_ERROR_MESSAGE = i18n._(t`Value is required`);

export default function EditSnmpTrapForm({
    selectedRecord = {},
    selectedList = [],
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
    selectedType,
    setSelectionIsDirty,
}) {
    const classes = useSettingStyles();
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: defaultFormValues });
    const internalId = useRef(null);
    const buttonText = useRef(addButtonText);
    const { handleSubmit, formState, reset, trigger, setValue, watch, getValues } = methods;
    const currentVersion = watch(SNMP_VERSION_FIELD);
    const currentProtocol = watch(SNMP_PROTOCOL_FIELD);
    const currentPrivacyProtocol = watch(SNMP_PRIVACYPROTOCOL_FIELD);
    const currentServer = watch(SNMP_SERVER_FIELD);
    const currentCommunityValue = watch(SNMP_COMMUNITY_VALUE_FIELD);
    const currentUserName = watch(SNMP_USERNAME_FIELD);
    const [saving, setSaving] = useState(false);
    const [selectionMode, setSelectionMode] = useState(SELECTION_MODE.all);
    const isNew = _.isEmpty(selectedRecord);
    const [validSelection, setValidSelection] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const selectedIds = watch(NOTIFY_NOTIFY_IDS_FIELD);

    useEffect(() => {
        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            buttonText.current = selectedRecord[INTERNAL_ID_FIELD] ? updateButtonText : addButtonText;

            const mode =
                selectedRecord.all || _.isEmpty(_.get(selectedRecord, 'ids', []))
                    ? SELECTION_MODE.all
                    : SELECTION_MODE.selection;
            setSelectionMode(mode);

            const trap = {
                ...selectedRecord,
                selectionMode: mode,
            };

            reset(trap);
        }
    }, [selectedRecord, reset]);

    const isSelectionModeValid = useCallback(() => {
        if (selectionMode === SELECTION_MODE.all) {
            return true;
        }

        return !_.isEmpty(getValues(NOTIFY_NOTIFY_IDS_FIELD));
    }, [selectionMode, getValues]);

    useEffect(() => {
        setValidSelection(isSelectionModeValid());
    }, [selectedIds, isSelectionModeValid]);

    useEffect(() => {
        if (formState.isSubmitted || formState.isDirty) {
            if (isSnmpV2c(currentVersion)) {
                trigger(SNMP_COMMUNITY_VALUE_FIELD);
            } else if (isSnmpV3(currentVersion)) {
                trigger(SNMP_USERNAME_FIELD);
            }
        }
    }, [currentServer, currentVersion, trigger, formState.isSubmitted, formState.isDirty]);

    useEffect(() => {
        if (formState.isSubmitted || formState.isDirty) {
            trigger(SNMP_SERVER_FIELD);
        }
    }, [currentCommunityValue, currentUserName, trigger, formState.isSubmitted, formState.isDirty]);

    const handleCleanup = () => {
        internalId.current = null;
        buttonText.current = addButtonText;
        setSelectedRecord(undefined);
        setSubmitAttempted(false);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (!isSelectionModeValid()) {
            setSubmitAttempted(true);
            return;
        }
        if (internalId) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        prepareTrapData(dataToSave);

        dataToSave.all = selectionMode === SELECTION_MODE.all;
        dataToSave.selectionMode = selectionMode;
        if (dataToSave.all) {
            dataToSave.ids = [];
        }

        setSaving(true);

        try {
            await onConfirm(dataToSave);
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const handleSelectionModeChange = (e) => {
        const newMode = e.target.value;
        setSelectionMode(newMode);
        setSubmitAttempted(false);
        setValidSelection(isSelectionModeValid());
    };

    const prepareTrapData = (dataToSave) => {
        dataToSave.server = _.trim(dataToSave.server);
        dataToSave.port = 162;

        const version = getSnmpVersionOrDefault(dataToSave.version);
        dataToSave.version = version;

        if (isSnmpV2c(version)) {
            prepareV2cTrapData(dataToSave);
        } else if (isSnmpV3(version)) {
            prepareV3TrapData(dataToSave);
        }
    };

    const prepareV2cTrapData = (dataToSave) => {
        // Trim password if it exists and has been modified
        if (dataToSave.community && dataToSave.community.value && dataToSave.community.value !== null) {
            dataToSave.community.value = _.trim(dataToSave.community.value);
        }

        // Delete unused v3 fields
        delete dataToSave.userName;
        delete dataToSave.context;
        delete dataToSave.protocol;
        delete dataToSave.authPassword;
        delete dataToSave.privacyProtocol;
        delete dataToSave.privacyPassword;
    };

    const prepareV3TrapData = (dataToSave) => {
        dataToSave.userName = _.trim(dataToSave.userName || '');
        dataToSave.context = _.trim(dataToSave.context || '');

        // Trim passwords if they exist and are not null
        if (dataToSave.authPassword && dataToSave.authPassword.value && dataToSave.authPassword.value !== null) {
            dataToSave.authPassword.value = _.trim(dataToSave.authPassword.value);
        }

        if (
            dataToSave.privacyPassword &&
            dataToSave.privacyPassword.value &&
            dataToSave.privacyPassword.value !== null
        ) {
            dataToSave.privacyPassword.value = _.trim(dataToSave.privacyPassword.value);
        }

        // Delete unused v2c field
        delete dataToSave.community;
    };

    const serverValid = {
        validate: (value) => serverValidations(value),
    };

    const communityValid = {
        validate: (value) => communityValidations(value),
    };

    const userNameValid = {
        validate: (value) => userNameValidations(value),
    };

    const serverValidations = (value) => {
        if (!IPAddressUtilities.validateDomainName(value)) return VALID_SERVER_ERROR_MESSAGE;
        if (hasDuplicateTrap()) return TRAP_EXIST_ERROR_MESSAGE;
        return true;
    };

    const communityValidations = (value) => {
        if (!trimCheck(value)) return VALUE_REQUIRED_ERROR_MESSAGE;
        if (hasDuplicateTrap()) return TRAP_EXIST_ERROR_MESSAGE;
        return true;
    };

    const userNameValidations = (value) => {
        if (!trimCheck(value)) return VALUE_REQUIRED_ERROR_MESSAGE;
        if (hasDuplicateTrap()) return TRAP_EXIST_ERROR_MESSAGE;
        return true;
    };

    const hasDuplicateTrap = () => {
        const currentTrap = getValues();
        const trimServer = _.trim(currentTrap.server);
        if (!trimServer) return false;
        const version = getSnmpVersionOrDefault(currentTrap.version);
        return checkForDuplicateTrap(trimServer, version);
    };

    const trimCheck = (value) => {
        return _.trim(value).length > 0;
    };

    const checkForDuplicateTrap = (trimServer, version) => {
        const index = selectedList.indexOf(selectedRecord);
        for (let i = 0; i < selectedList.length; i++) {
            if (i !== index && trimServer === _.trim(selectedList[i].server)) {
                const trapVersion = getSnmpVersionOrDefault(_.get(selectedList[i], 'version'));
                if (version === trapVersion && isDuplicateCredential(version, selectedList[i])) {
                    return true;
                }
            }
        }
        return false;
    };

    const isDuplicateCredential = (version, existingTrap) => {
        const currentTrap = getValues();

        if (isSnmpV2c(version)) {
            const communityValue = currentTrap.community?.value;
            const trimCommunity = _.trim(communityValue);
            return trimCommunity === _.trim(existingTrap.community?.value);
        }
        if (isSnmpV3(version)) {
            const userNameValue = currentTrap.userName;
            const userName = _.trim(userNameValue);
            const trapUserName = _.trim(existingTrap.userName || '');
            return userName === trapUserName;
        }
        return false;
    };

    const onVersionChanged = (value) => {
        const version = getSnmpVersionOrDefault(value);

        const currentTrap = getValues();
        const newTrap = _.cloneDeep(currentTrap);
        newTrap.version = version;
        if (isSnmpV2c(version)) {
            // Reset v3 fields
            newTrap.userName = '';
            newTrap.context = '';
            newTrap.protocol = 'None';
            newTrap.authPassword = {};
            newTrap.privacyProtocol = 'None';
            newTrap.privacyPassword = {};
        } else if (isSnmpV3(version)) {
            // Reset v2c fields
            newTrap.community = {};
            // Set default protocol if not set
            if (!currentTrap.protocol) {
                newTrap.protocol = 'None';
            }
            if (!currentTrap.privacyProtocol) {
                newTrap.privacyProtocol = 'None';
            }
        }

        reset(newTrap);
    };

    const onProtocolChanged = (value) => {
        setValue(SNMP_PROTOCOL_FIELD, value, { shouldDirty: true });
        if (value === 'None' || isNew) {
            // Clear password fields and reset privacy protocol
            setValue(SNMP_AUTHPASSWORD_VALUE_FIELD, '', { shouldDirty: true });
            setValue(SNMP_PRIVACYPROTOCOL_FIELD, 'None', { shouldDirty: true });
            setValue(SNMP_PRIVACYPASSWORD_VALUE_FIELD, '', { shouldDirty: true });
        }
    };

    const onPrivacyProtocolChanged = (value) => {
        setValue(SNMP_PRIVACYPROTOCOL_FIELD, value, { shouldDirty: true });
        if (value === 'None') {
            setValue(SNMP_PRIVACYPASSWORD_VALUE_FIELD, '', { shouldDirty: true });
        }
    };

    const isPasswordRequired = (name) => {
        if (isNew) {
            return isPasswordRequiredForNew(name);
        }
        return isPasswordRequiredForExisting(name);
    };

    const isPasswordRequiredForNew = (name) => {
        const currentTrap = getValues();
        if (name === 'authPassword') {
            return currentTrap.protocol !== 'None';
        }
        if (name === 'privacyPassword') {
            return currentTrap.protocol !== 'None' && currentTrap.privacyProtocol !== 'None';
        }
        return false;
    };

    const isPasswordRequiredForExisting = (name) => {
        if (name === 'authPassword') {
            return isAuthPasswordRequired();
        }
        if (name === 'privacyPassword') {
            return isPrivacyPasswordRequired();
        }
        return false;
    };

    const isAuthPasswordRequired = () => {
        const originalProtocol = selectedRecord.protocol || 'None';
        return originalProtocol === 'None' && currentProtocol !== 'None';
    };

    const isPrivacyPasswordRequired = () => {
        const originalPrivacyProtocol = selectedRecord.privacyProtocol || 'None';
        return originalPrivacyProtocol === 'None' && currentProtocol !== 'None';
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={
                selectedRecord?.[INTERNAL_ID_FIELD] ? (
                    <Trans>Edit SNMP Trap Notification</Trans>
                ) : (
                    <Trans>Add SNMP Trap Notification</Trans>
                )
            }
            confirmButtonText={<Trans>Ok</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            maxWidth={false}
            paperProps={{ className: classes.snmpDialog }}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpSelect
                            className={classes.snmpSelection}
                            fieldName={SNMP_VERSION_FIELD}
                            label={<Trans>Version</Trans>}
                            required
                            onChangeFunc={(e) => onVersionChanged(e?.target?.value, e)}
                        >
                            {_.map(SNMP_VERSION_OPTIONS, (value) => (
                                <MenuItem key={value.value} value={value.value}>
                                    {value.display}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                        <NgpTextField
                            fieldName={SNMP_SERVER_FIELD}
                            label={<Trans>SNMP Collector</Trans>}
                            required
                            fieldRules={serverValid}
                            inputProps={{
                                size: 60,
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                        {isSnmpV2c(currentVersion) && (
                            <NgpPasswordField
                                fieldName={SNMP_COMMUNITY_VALUE_FIELD}
                                label={<Trans>Community</Trans>}
                                fieldRules={communityValid}
                                inputProps={{ size: 60, maxLength: 50 }}
                                required
                                togglePasswordVisibility
                            />
                        )}
                        {isSnmpV3(currentVersion) && (
                            <>
                                <NgpTextField
                                    fieldName={SNMP_USERNAME_FIELD}
                                    label={<Trans>User Name</Trans>}
                                    required
                                    fieldRules={userNameValid}
                                    inputProps={{
                                        size: 60,
                                        maxLength: 45,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                />
                                <NgpTextField
                                    fieldName={SNMP_CONTEXT_FIELD}
                                    label={<Trans>Context</Trans>}
                                    inputProps={{
                                        size: 60,
                                        maxLength: 45,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                />
                                <NgpSelect
                                    className={classes.snmpSelection}
                                    fieldName={SNMP_PROTOCOL_FIELD}
                                    label={<Trans>Authentication Protocol</Trans>}
                                    required
                                    onChangeFunc={(e) => onProtocolChanged(e?.target?.value, e)}
                                >
                                    {_.map(SNMPV3_PROTOCOLS, (value) => (
                                        <MenuItem key={value} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </NgpSelect>
                                {currentProtocol !== 'None' && (
                                    <>
                                        <NgpPasswordField
                                            fieldName={SNMP_AUTHPASSWORD_VALUE_FIELD}
                                            label={<Trans>Authentication Password</Trans>}
                                            inputProps={{
                                                size: 60,
                                                maxLength: 255,
                                                autoComplete: AUTO_COMPLETE_DEFAULT,
                                            }}
                                            required={isPasswordRequired('authPassword')}
                                            togglePasswordVisibility
                                        />

                                        <NgpSelect
                                            className={classes.snmpSelection}
                                            fieldName={SNMP_PRIVACYPROTOCOL_FIELD}
                                            label={<Trans>Privacy Protocol</Trans>}
                                            required
                                            onChangeFunc={(e) => onPrivacyProtocolChanged(e?.target?.value, e)}
                                        >
                                            {_.map(SNMPV3_PRIVACY_PROTOCOLS, (value) => (
                                                <MenuItem key={value} value={value}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </NgpSelect>
                                        {currentPrivacyProtocol !== 'None' && (
                                            <NgpPasswordField
                                                fieldName={SNMP_PRIVACYPASSWORD_VALUE_FIELD}
                                                label={<Trans>Privacy Password</Trans>}
                                                inputProps={{
                                                    size: 60,
                                                    maxLength: 255,
                                                    autoComplete: AUTO_COMPLETE_DEFAULT,
                                                }}
                                                required={isPasswordRequired('privacyPassword')}
                                                togglePasswordVisibility
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        <RadioGroup
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                            style={{ marginLeft: 4, marginTop: 16 }}
                        >
                            <FormControlLabel
                                value={SELECTION_MODE.all}
                                label={t`All ${selectedType.titlePlural}`}
                                control={<Radio size="small" style={{ padding: 2 }} />}
                                style={{ marginBottom: 4, marginTop: 4 }}
                            />
                            <FormControlLabel
                                value={SELECTION_MODE.selection}
                                label={t`Selected ${selectedType.titlePlural}`}
                                control={<Radio size="small" style={{ padding: 2 }} />}
                                style={{ marginBottom: 16, marginTop: 4 }}
                            />
                        </RadioGroup>
                        {selectionMode === SELECTION_MODE.selection && !validSelection && submitAttempted && (
                            <h3 className={classes.selectionError}>
                                One or more {selectedType.titlePlural} need to be added to this notification
                            </h3>
                        )}
                        <div
                            style={{
                                minHeight: 250,
                                display: selectionMode === SELECTION_MODE.selection ? undefined : 'none',
                            }}
                        >
                            <ListSelectorField
                                gridColumns={getDialogTableColumns(selectedType)}
                                sorters={initialSort}
                                dbSelected={selectedRecord[NOTIFY_NOTIFY_IDS_FIELD]}
                                selectedDataSource={
                                    getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.selectedDataSource]
                                }
                                availableDataSource={
                                    getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.availableDataSource]
                                }
                                idsDataSource={getDialogTableDataSource(selectedType)[DATASOURCE_TYPE.idsDataSource]}
                                fieldName={NOTIFY_NOTIFY_IDS_FIELD}
                                addButtonText={getAddButtonText(selectedType)}
                                setSelectionIsDirty={setSelectionIsDirty}
                                isMultiRemove={false}
                                selectedEmptyGridText={getEmptyGridText(selectedType)}
                            />
                        </div>
                        {/* )} */}
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
